import { SyntheticEvent, useState, useEffect, memo, useRef } from "react";
import {
  Form,
  FormGroup,
  FormField,
  Dropdown,
  Input,
  DropdownProps,
  InputOnChangeData,
  Icon,
} from "semantic-ui-react";

// Serializers
import FormCategorySerializer from "@/issara-sdk/types/FormCategorySerializer_apps_PRX";

// Types
import { CreatedData } from "./MeFormType";

// Interface
import { FORM_TYPE_OPTIONS } from "./sequence/MeForm";
import { OptionType } from "../HISV3/common/CommonInterface";

type ModAddMeFormContentProps = {
  isEdit?: boolean;
  createdData?: CreatedData;
  formCategoryList: FormCategorySerializer[];
  onChangeData: (e: SyntheticEvent, data: DropdownProps | InputOnChangeData) => void;
};

const FORM_TYPE_DESCRIPTIONS = [
  {
    label: "Decision Tree (แบบฟอร์มเลือกเส้นทาง)",
    description: "ระบบคำถามต่อเนื่องที่เปลี่ยนไปตามคำตอบจริงของผู้ใช้งาน",
  },
];

//* ========== FORM TYPE POPUP ========== *//

const FormTypeInfoPopover = () => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div
      style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
      ref={popoverRef}
    >
      {/* Info Icon Button */}
      <div
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          backgroundColor: "#E8768A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <Icon
          name="info"
          style={{
            color: "#fff",
            fontSize: "12px",
            margin: 0,
            lineHeight: 1,
          }}
        />
      </div>

      {/* Popover Card */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 10px)",
            left: "0",
            zIndex: 1000,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            padding: "14px 16px",
            minWidth: "300px",
            maxWidth: "360px",
          }}
        >
          {/* Arrow */}
          <div
            style={{
              position: "absolute",
              top: "-8px",
              left: "7px",
              width: 0,
              height: 0,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderBottom: "8px solid #fff",
              filter: "drop-shadow(0 -2px 2px rgba(0,0,0,0.08))",
            }}
          />

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  backgroundColor: "#E8768A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="info" style={{ color: "#fff", fontSize: "10px", margin: 0 }} />
              </div>
              <span style={{ fontWeight: "600", fontSize: "14px", color: "#333" }}>
                รูปแบบของ Form Type:
              </span>
            </div>
            <Icon
              name="close"
              style={{ cursor: "pointer", color: "#999", fontSize: "14px", margin: 0 }}
              onClick={() => setOpen(false)}
            />
          </div>

          {/* Content */}
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            {FORM_TYPE_DESCRIPTIONS.map((item, idx) => (
              <li key={idx} style={{ marginBottom: "4px" }}>
                <span style={{ fontWeight: "600", fontSize: "13px", color: "#222" }}>
                  {item.label}
                </span>
                <br />
                <span style={{ fontSize: "13px", color: "#555" }}>{item.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

//* ========== MAIN MOD ========== *//

const ModAddMeFormContent = (props: ModAddMeFormContentProps) => {
  const { isEdit, createdData } = props;

  const [allCategoryOptions, setAllCategoryOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    const defaultValue = createdData?.form_type || "DECISION_TREE";

    props.onChangeData(
      {} as React.SyntheticEvent,
      {
        name: "form_type",
        value: defaultValue,
      } as DropdownProps,
    );
  }, []);

  useEffect(() => {
    const activeCategories = props.formCategoryList.filter((cat) => cat.active);

    const formattedOptions = activeCategories.map((cat) => ({
      key: cat.id,
      value: cat.name,
      text: cat.name,
    })) as OptionType[];

    const existing = createdData?.category_name_list || [];
    const addOptions = existing
      .filter((name) => !formattedOptions.some((opt) => opt.value === name))
      .map((name, idx) => ({
        key: `ext-${idx}`,
        value: name,
        text: name,
      }));

    setAllCategoryOptions([...formattedOptions, ...addOptions]);
  }, [props.formCategoryList, createdData?.category_name_list]);

  const handleAddCategory = (e: SyntheticEvent, { value }: { value?: any }) => {
    if (!value || typeof value !== "string") return;

    const exists = allCategoryOptions.some((opt) => opt.value === value);

    if (!exists) {
      setAllCategoryOptions((prev) => [...prev, { key: prev.length + 1, value, text: value }]);
    }

    const current = createdData?.category_name_list || [];
    const newValue = current.includes(value) ? current : [...current, value];

    props.onChangeData(e, {
      name: "category_name_list",
      value: newValue,
    } as DropdownProps);
  };

  return (
    <Form>
      <FormGroup inline>
        <FormField inline width={6}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <label style={{ fontWeight: "bold", fontSize: "0.9285rem" }}>
              Form Type <span style={{ color: "red" }}>*</span>
            </label>
          </div>
          <FormTypeInfoPopover />
        </FormField>
        <FormField inline width={10}>
          <div style={{ width: "100%" }}>
            <Dropdown
              disabled={Boolean(isEdit)}
              name="form_type"
              selection
              fluid
              placeholder="Select form type"
              value={createdData?.form_type || "DECISION_TREE"}
              onChange={props.onChangeData}
              options={FORM_TYPE_OPTIONS}
            />
          </div>
        </FormField>
      </FormGroup>

      <FormGroup inline>
        <FormField inline width={6}>
          <label>
            Form Category <span style={{ color: "red" }}>*</span>
          </label>
        </FormField>
        <FormField inline width={10}>
          <div style={{ width: "100%" }}>
            <Dropdown
              name="category_name_list"
              selection
              fluid
              multiple
              search
              allowAdditions
              placeholder="เลือก หรือพิมพ์เพื่อเพิ่ม Category ใหม่"
              value={createdData?.category_name_list || []}
              onChange={props.onChangeData}
              onAddItem={handleAddCategory}
              options={allCategoryOptions}
            />
          </div>
        </FormField>
      </FormGroup>

      <FormGroup inline>
        <FormField inline width={6}>
          <label>
            Form Name <span style={{ color: "red" }}>*</span>
          </label>
        </FormField>
        <FormField inline width={10}>
          <div style={{ width: "100%" }}>
            <Input
              name="name"
              fluid
              value={createdData?.name || ""}
              onChange={props.onChangeData}
            />
          </div>
        </FormField>
      </FormGroup>
    </Form>
  );
};

ModAddMeFormContent.displayName = "ModAddMeFormContent";

export default memo(ModAddMeFormContent);
